import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert, Switch, FormControlLabel,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { disksAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Disk } from '../../types';

const formatDiskSize = (size: number): string => {
  if (size >= 1024) return `${(size / 1024).toFixed(size % 1024 === 0 ? 0 : 1)}TB`;
  return `${size}GB`;
};

interface DiskFormData { diskSize: string; displayOrder: number; isActive: boolean; }

const Disks = () => {
  const [disks, setDisks] = useState<Disk[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDisk, setEditingDisk] = useState<Disk | null>(null);
  const [formData, setFormData] = useState<DiskFormData>({ diskSize: '', displayOrder: 0, isActive: true });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchDisks(); }, []);

  const fetchDisks = async () => {
    try { const response = await disksAPI.getAll(); setDisks(response.data); setError(''); }
    catch (err) { setError('Failed to load disks'); } finally { setLoading(false); }
  };

  const handleOpenDialog = (disk: Disk | null = null) => {
    if (disk) { setEditingDisk(disk); setFormData({ diskSize: String(disk.DiskSize), displayOrder: disk.DisplayOrder, isActive: !!disk.IsActive }); }
    else { setEditingDisk(null); setFormData({ diskSize: '', displayOrder: 0, isActive: true }); }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditingDisk(null); setFormData({ diskSize: '', displayOrder: 0, isActive: true }); };

  const handleSubmit = async () => {
    try {
      if (editingDisk) { await disksAPI.update(editingDisk.DiskId, formData); } else { await disksAPI.create(formData); }
      fetchDisks(); handleCloseDialog(); setError('');
    } catch (err: unknown) { const axiosErr = err as { response?: { data?: { error?: string } } }; setError(axiosErr.response?.data?.error || 'Failed to save disk'); }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { await disksAPI.delete(id); fetchDisks(); setError(''); } catch (err) { setError('Failed to delete disk'); }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Disk Sizes</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()}>Add Disk Size</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Disk Size</TableCell><TableCell>Display Order</TableCell><TableCell>Active</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
          <TableBody>
            {disks.map((disk) => (
              <TableRow key={disk.DiskId}>
                <TableCell>{formatDiskSize(disk.DiskSize)}</TableCell><TableCell>{disk.DisplayOrder}</TableCell><TableCell>{disk.IsActive ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(disk)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => handleDeleteClick(disk.DiskId)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingDisk ? 'Edit Disk Size' : 'Add Disk Size'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" size="small" label="Disk Size (GB)" type="number" fullWidth value={formData.diskSize} onChange={(e) => setFormData({ ...formData, diskSize: e.target.value })} required inputProps={{ min: 1 }} />
          <TextField margin="dense" size="small" label="Display Order" type="number" fullWidth value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} />
          <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!formData.diskSize}>{editingDisk ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate Disk Size"
        message="Are you sure you want to deactivate this disk size?"
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default Disks;
